import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api, authApi, type Role } from './api';

interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  /** Optional for back-compat with sessions stored before this field existed. */
  email_notifications?: boolean;
}

interface LicenseStatus {
  plan: string;
  tier: 'standard' | 'pro';
  expiresAt: string | null;
  graceDays: number | null;
  warningDays: number | null;
  warningType: 'trial' | 'license' | 'grace' | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  timezone: string;
  needsSetup: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  isAdmin: boolean;
  isSupervisor: boolean;
  licenseStatus: LicenseStatus | null;
  /** True if the tenant has access to Pro-tier features (Pro SaaS, on-prem, or demo). */
  hasPro: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,          setUser]          = useState<AuthUser | null>(null);
  const [timezone,      setTimezone]      = useState<string>(() => localStorage.getItem('vmt_timezone') ?? 'UTC');
  const [needsSetup,    setNeedsSetup]    = useState<boolean>(false);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);

  // Re-hydrate from localStorage, validate the session cookie, and check setup status.
  useEffect(() => {
    // Check whether this is a fresh install with no users yet.
    api.get<{ needs_setup: boolean }>('/setup/status')
      .then(data => setNeedsSetup(data.needs_setup))
      .catch(() => {}); // network error — don't block the app

    const stored = localStorage.getItem('vmt_user');
    if (!stored) return;
    try {
      setUser(JSON.parse(stored));
    } catch {
      localStorage.removeItem('vmt_user');
      return;
    }
    // Background validation — if the HttpOnly cookie has expired the 401 handler
    // in api.ts will clear localStorage and redirect to /login automatically.
    api.get<AuthUser>('/auth/me').catch(() => {});
    api.get<LicenseStatus>('/license/status').then(setLicenseStatus).catch(() => {});
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tz  = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const res = await authApi.login(email, password, tz);
    localStorage.setItem('vmt_user',     JSON.stringify(res.user));
    localStorage.setItem('vmt_timezone', res.timezone);
    setUser(res.user);
    setTimezone(res.timezone);
    setNeedsSetup(false); // setup is now complete
    api.get<LicenseStatus>('/license/status').then(setLicenseStatus).catch(() => {});
  }, []);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('vmt_user', JSON.stringify(next));
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('vmt_user');
    localStorage.removeItem('vmt_timezone');
    setUser(null);
    setTimezone('UTC');
    // Fire-and-forget — clears the HttpOnly cookie server-side
    authApi.logout().catch(() => {});
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      timezone,
      needsSetup,
      login,
      logout,
      updateUser,
      isAdmin:      user?.role === 'admin',
      isSupervisor: user?.role === 'admin' || user?.role === 'supervisor',
      licenseStatus,
      // Pro features available on Pro SaaS, on-prem (always), or demo (trial gets everything).
      // When license hasn't loaded yet, fail closed (no Pro UI shown).
      hasPro:
        licenseStatus?.plan === 'onprem' ||
        licenseStatus?.plan === 'demo' ||
        (licenseStatus?.plan === 'cloud' && licenseStatus?.tier === 'pro'),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
